import useGetPaymentProviderStatusQuery from '@/api/queries/useGetPaymentProviderStatusQuery';
import { isBillingEnabledFor } from '@/config';
import {
  BillingDetailsEditPath,
  BillingHomePath,
  BillingPaymentMethodAddPath,
  BillingPaymentMethodListPath,
} from '@/modules/billing/paths';
import {
  ProductAddPath,
  ProductEditPath,
  ProductImportPath,
  ProductListPath,
  PromotionAddPath,
  PromotionEditPath,
  PromotionListPath,
} from '@/modules/product/paths';
import { Settings as BaseSettings, Section } from '@/modules/settings/pages/Settings';
import { CustomerListPath, SupplierSettingsPath } from '@/modules/supplier/paths';
import {
  BellIcon,
  BuildingOfficeIcon,
  CreditCardIcon,
  CubeIcon,
  LockClosedIcon,
  PercentBadgeIcon,
  UserIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { Outlet } from 'react-router-dom';

const isSection = (value: Section | undefined): value is Section => Boolean(value);

const sections: (paymentStatus?: boolean) => Section[] = (paymentStatus) =>
  [
    {
      href: SupplierSettingsPath.profile,
      title: 'Profile',
      icon: UserIcon,
      children: [
        { href: SupplierSettingsPath.profile, title: 'Profile Details', icon: UserIcon },
        { href: SupplierSettingsPath.notification, title: 'Notifications', icon: BellIcon },
        // {href: SupplierSettingsPath.currency, title: 'Currency', icon: CurrencyDollarIcon},
        // {href: SupplierSettingsPath.language, title: 'Language', icon: LanguageIcon},
        { href: SupplierSettingsPath.password, title: 'Change Password', icon: LockClosedIcon },
        {
          href: SupplierSettingsPath.businessAddress,
          title: 'Business Address',
          icon: BuildingOfficeIcon,
        },
      ],
    },
    paymentStatus && isBillingEnabledFor('supplier')
      ? {
          href: BillingHomePath,
          title: 'Billing',
          icon: CreditCardIcon,
          children: [
            {
              href: BillingHomePath,
              title: 'Billing',
              icon: CreditCardIcon,
              children: [
                {
                  href: BillingDetailsEditPath,
                  title: 'Edit Billing Details',
                  icon: CreditCardIcon,
                },
              ],
            },
            {
              href: BillingPaymentMethodListPath,
              title: 'Payment Methods',
              icon: CreditCardIcon,
              children: [
                {
                  href: BillingPaymentMethodAddPath,
                  title: 'Add Payment Method',
                  icon: CreditCardIcon,
                },
              ],
            },
          ],
        }
      : undefined,
    {
      href: ProductListPath,
      title: 'Products',
      icon: CubeIcon,
      children: [
        {
          href: ProductListPath,
          title: 'Products',
          icon: CubeIcon,
          children: [
            { href: ProductAddPath, title: 'Add Product', icon: CubeIcon },
            { href: ProductEditPath.path, title: 'Edit Product', icon: CubeIcon },
            { href: ProductImportPath, title: 'Import Products', icon: CubeIcon },
          ],
        },
        {
          href: PromotionListPath,
          title: 'Promotions',
          icon: PercentBadgeIcon,
          children: [
            { href: PromotionAddPath, title: 'Add Promotion', icon: PercentBadgeIcon },
            {
              href: PromotionEditPath.path,
              title: 'Edit Promotion',
              icon: PercentBadgeIcon,
            },
          ],
        },
      ],
    },
    { href: CustomerListPath, title: 'Customers', icon: UsersIcon },
  ].filter(isSection);

const Settings = () => {
  const { status: paymentStatus, isLoading } = useGetPaymentProviderStatusQuery();

  if (isLoading) {
    return;
  }

  return (
    <BaseSettings sections={sections(paymentStatus?.available)}>
      <Outlet />
    </BaseSettings>
  );
};

export default Settings;
